import Msg from "./message";
import Entity from "./entity";
import Auth from "./auth";
import Api from "./api";
import { deepDecode, deepEncode, getPathWeight } from "../utils";
import IRelationPathField from "../interface/relation-path-field";
import IRelationPath from "../interface/relation-path";
import IConfigFormulaRenewal from "../interface/field-cfg-formula-renewal";
import IConfigFormula from "../interface/field-cfg-formula";
import IConfigDatetime from "../interface/field-cfg-datetime";
import IField from "../interface/field";
import { IConnectionField } from "../interface/data-connection";
import IApiPointer from "../interface/api/api-pointer";
import IApiField from "../interface/api/api-field";
import IApiEntity from "../interface/api/api-entity";
import Global from "../global";
import Message from "../enum/message";
import FormulaType from "../enum/formula-type";
import { DefaultRefTypes, ValuableTypes } from "../enum/field-types";
import FieldType from "../enum/field-type";
import FieldSystemType from "../enum/field-system-type";
import ErrorCode from "../enum/error-code";
import DefaultConfig from "../default-config";

class FieldInstance {

    /* Public Methods */

    public get(fieldId: number): IField | null {
        const apiField: IApiField | null = Api.getField(fieldId);
        if (apiField) {
            return this.toClientFormat(apiField);
        }
        return null;
    }

    public async create(entityId: number, name: string = Msg.get(Message.defaultfieldname)): Promise<IField> {
        if (Auth.isAuthorized()) {
            const entity: IApiEntity | null = Api.getEntity(entityId);
            if (entity) {
                const singular = deepEncode(name.trim());
                if (singular) {
                    const result: IApiField = await Api.fieldCreate(entity, singular);
                    return this.get(result.id) as IField;
                } else {
                    throw ErrorCode.BadArguments;
                }
            } else {
                throw ErrorCode.NoEntity;
            }
        } else {
            throw ErrorCode.AccessDenied;
        }
    }

    public async rename(fieldId: number, name: string): Promise<IField> {
        if (Auth.isAuthorized()) {
            const field: IApiField | null = Api.getField(fieldId);
            if (field) {
                name = name.trim();
                if (name) {
                    field.singular = deepEncode(name);
                    await Api.fieldSave(field);
                    return this.get(field.id) as IField;
                } else {
                    throw ErrorCode.BadArguments;
                }
            } else {
                throw ErrorCode.NoField;
            }
        } else {
            throw ErrorCode.AccessDenied;
        }
    }

    public async delete(fieldId: number): Promise<number> {
        if (Auth.isAuthorized()) {
            const field: IApiField | null = Api.getField(fieldId);
            if (field) {
                await Api.fieldDelete(field);
                return fieldId;
            } else {
                throw ErrorCode.NoField;
            }
        } else {
            throw ErrorCode.AccessDenied;
        }
    }

    /**
    * Returns list of fields which use same pointer as selected linked field.
    * @param {object} fieldId - Linked Field.
    */
    public getRelatedFields(fieldId: number): string[] {
        const result: string[] = [];
        const field: IField | null = this.get(fieldId);
        if (field && field.uiType === FieldType.linked) {
            const entity: IApiEntity = Api.getEntity(field.entityId) as IApiEntity;
            entity.fields.forEach((eField: IApiField) => {
                const ePointer = eField.linkedRelation && eField.linkedRelation[0];
                if (field.linkedRelation[0] === ePointer && field.id !== eField.id) {
                    result.push(eField.singular);
                }
            });
        }
        return result;
    }

    public getLookupOptions(fieldId: number): IRelationPath[] {
        const field: IField | null = this.get(fieldId);
        const skipPointers: number[] = [];
        const deep: number = 1;
        if (field) {
            if (field.uiType === FieldType.linked) {
                skipPointers.push(...field.linkedRelation);
            }
            return this.getRelationPaths(field.entityId, ValuableTypes, deep, skipPointers);
        }
        return [];
    }

    public getTypeReferenced(entityId: number, type: FieldType): IRelationPathField[] {
        return this.getRelationPathsFields(entityId, [type], 2, true);
    }

    /**
    * Returns a list of available default references for selected field.
    * @param {object} fieldId - Selected field.
    */
    public getDefaultValueOptions(fieldId: number): IRelationPathField[] {
        const field: IField | null = this.get(fieldId);
        if (field && DefaultRefTypes.includes(field.uiType)) {
            let result: IRelationPathField[] = [];
            result = this.getRelationPathsFields(field.entityId, [field.uiType], 1);
            if (field.uiType === FieldType.datetime) {
                result.push(...this.getRelationPathsFields(field.entityId, [FieldType.formula], 1).filter(r => {
                    const f: IField = this.get(r.fieldId) as IField;
                    const config: IConfigFormula = f.uiConfig as IConfigFormula;
                    if (config.type === FormulaType.renewal) {
                        const formula: IConfigFormulaRenewal = config.formula as IConfigFormulaRenewal;
                        return !formula.showPeriod;
                    }
                    return false;
                }));
            } else if (field.uiType === FieldType.number) {
                result.push(...this.getRelationPathsFields(field.entityId, [FieldType.formula], 1).filter(r => {
                    const f: IField = this.get(r.fieldId) as IField;
                    return (f.uiConfig as IConfigFormula).type === FormulaType.number;
                }));
            } else if (field.uiType === FieldType.linked) {
                result = result.filter((r: IRelationPathField) => {
                    const f: IField = this.get(r.fieldId) as IField;
                    return f.foreignerEcn === field.foreignerEcn;
                });
            }
            return result.filter(f => f.fieldId !== fieldId || !!f.path.length).sort((a, b) => {
                return getPathWeight(a.pathId) - getPathWeight(b.pathId);
            });
        }
        return [];
    }

    public getDefaultValue(fieldId: number): number[] | null {
        const field: IApiField | null = Api.getField(fieldId);
        if (field && field.UI.default) {
            // TODO - Replace UI with another prop
            const id: string = field.UI.default.join(',');
            const opts: IRelationPathField[] = this.getDefaultValueOptions(fieldId);
            const opt: IRelationPathField | null = opts.find(o => o.id === id) || null;
            if (opt) {
                return field.UI.default as number[];
            }
        }
        return null;
    }

    public async setDefaultValue(fieldId: number, value: number[] | null): Promise<IField> {
        const field: IApiField | null = Api.getField(fieldId);
        if (field) {
            // TODO - Replace UI with another prop
            field.UI = {};
            field.UI.default = value;
            await Api.fieldSave(field);
            return this.get(fieldId) as IField;
        } else {
            throw ErrorCode.BadArguments;
        }
    }

    /* Private Public Methods */

    public getSystemType(type: FieldType): FieldSystemType {
        switch(type) {
            case FieldType.linked:
            case FieldType.lookup:
                return FieldSystemType.linked;
            case FieldType.pointer:
                return FieldSystemType.pointer;
            case FieldType.number:
                return FieldSystemType.number;
            case FieldType.uid:
                return FieldSystemType.uid;
            case FieldType.rollup:
                return FieldSystemType.rollup;
            default:
                return FieldSystemType.string;
        }
    }

    public getRelationPaths(entityId: number, types: FieldType[], maxLevel: number = 2, skipPointers: number[] = [], includeCurrent: boolean = false, skipCircuits = false, level: number = 0, skipEntities: number[] = []): IRelationPath[] {
        const relations: IRelationPath[] = [];
        const separator: string = ' - ';
        if (includeCurrent) {
            const relation: IRelationPath = {
                id: Global.RELATION_CURRENT_ID,
                path: [],
                ecn: entityId,
                name: '',
                fields: []
            };
            relation.fields = this.toRelationPathFields(Entity.getFieldsByType(entityId, types), relation, separator);
            if (relation.fields.length) {
                relations.push(relation);
            }
        }
        if (skipCircuits) {
            skipEntities.push(entityId);
        }
        if (!maxLevel || level < maxLevel) {
            Entity.getFieldsByType(entityId, [FieldType.linked]).forEach((field: IField) => {
                const ptField: IApiField = Api.getField(field.linkedRelation[0]) as IApiField;
                const foreignEntityId: number = (ptField.pointer as IApiPointer[])[1].ptEcn;
                if (!skipPointers.includes(ptField.id) && !skipEntities.includes(foreignEntityId)) {
                    const ecn: number = (ptField.pointer as IApiPointer[])[1].ptEcn;
                    const relation: IRelationPath = {
                        id: field.linkedRelation[0].toString(),
                        path: [field.linkedRelation[0]],
                        ecn: ecn,
                        name: field.singular,
                        fields: []
                    };
                    relation.fields = this.toRelationPathFields(Entity.getFieldsByType(ecn, types), relation, separator);
                    if (relation.fields.length) {
                        relations.push(relation);
                    }
                    this.getRelationPaths(ecn, types, maxLevel, skipPointers, false, skipCircuits, level + 1, [...skipEntities]).forEach((rel: IRelationPath) => {
                        rel.name = `${relation.name}${separator}${rel.name}`;
                        rel.path = relation.path.concat(rel.path);
                        rel.id = rel.path.join('-');
                        rel.fields = this.toRelationPathFields(Entity.getFieldsByType(rel.ecn, types), rel, separator);
                        relations.push(rel);
                    });
                }
            });
        }
        relations.forEach((path: IRelationPath) => {
            const field: IField = Field.get(path.fields[0].fieldId) as IField;
            const order: number[] = Entity.getDefaultFieldsOrder(field.entityId);
            path.fields.sort((a: IRelationPathField, b: IRelationPathField) => {
                return order.indexOf(a.fieldId) - order.indexOf(b.fieldId);
            });
        });
        return relations;
    }

    public getRelationPathsFields(entityId: number, types: FieldType[], maxLevel: number, skipCircuits: boolean = false): IRelationPathField[] {
        const result: IRelationPathField[] = [];
        const paths: IRelationPath[] = this.getRelationPaths(entityId, types, maxLevel, [], true, skipCircuits);
        paths.forEach((path: IRelationPath) => result.push(...path.fields));
        return result;
    }

    public getDatetimeConfig(field: IField): IConfigDatetime {
        if (field.uiType === FieldType.datetime) {
            return field.uiConfig as IConfigDatetime;
        } else if (field.uiType === FieldType.formula) {
            const config: IConfigFormula = field.uiConfig as IConfigFormula;
            const renewal: IConfigFormulaRenewal = config.formula as IConfigFormulaRenewal;
            if (config.type === FormulaType.renewal && !renewal.showPeriod) {
                return config.format as IConfigDatetime;
            }
        }
        return DefaultConfig[FieldType.datetime]();
    }

    public async setConnection(fieldId: number, connection: IConnectionField | null): Promise<IField | null> {
        const field: IApiField | null = Api.getField(fieldId);
        if (field) {
            field.UI.connection = connection;
            await Api.fieldSave(field);
            return this.get(fieldId);
        }
        return null;
    }

    /* Private Methods */

    private toClientFormat(apiField: IApiField): IField {
        const result: IField = {
            id: apiField.id,
            singular: deepDecode(apiField.singular),
            uiType: apiField.uiType,
            uiConfig: apiField.uiConfig,
            systemType: apiField.systemType,
            entityId: apiField.eCN,
            foreignerEcn: apiField.foreignerEcn || 0,
            linkedRelation: apiField.linkedRelation || [],
            foreignerFCNs: apiField.foreignerFCNs || [],
            foreignerFields: [],
            linkedTemplate: apiField.linkedTemplate || '',
            connection: apiField.UI && apiField.UI.connection ? apiField.UI.connection : null,
            pointer: apiField.pointer || []
        };
        switch(result.uiType) {
            case FieldType.number:
                result.uiConfig = apiField.uiConfig;
                break;
            case FieldType.phone:
                result.uiConfig = apiField.uiConfig;
                break;
            case FieldType.datetime:
                result.uiConfig = apiField.uiConfig;
                break;
            case FieldType.linked:
                result.uiConfig = apiField.uiConfig;
                break;
            case FieldType.lookup:
                result.foreignerFCNs.forEach((fieldId: number) => {
                    const field: IField | null = this.get(fieldId);
                    if (field) {
                        result.foreignerFields.push(field);
                    }
                });
                break;
        }
        return result;
    }

    private toRelationPathFields(fields: IField[], relation: IRelationPath, separator: string): IRelationPathField[] {
        return fields.map((field: IField) => {
            let pathName = field.singular;
            if (relation.name) {
                pathName = `${relation.name}${separator}${field.singular}`;
            }
            return {
                id: [...relation.path, field.id].join(','),
                fieldId: field.id,
                name: field.singular,
                path: relation.path,
                pathId: [...relation.path, field.id],
                pathName: pathName,
                type: field.uiType
            };
        })
    }

}

const Field = new FieldInstance();

export default Field;